import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { RaspaditasFisicasContentDataSource } from './raspaditas-fisicas-content-data-source';
import { ContentPage } from '../../models/content/content-page.model';
import {
  GanadorRaspadita,
  RaspaditaCatalogo,
  RaspaditaGame,
} from '../../../modules/raspaditas-fisicas/models/raspadita-game.model';

interface PublicRaspaditaFisicaDto {
  id: string;
  idJuego: string;
  slug: string;
  nombre: string;
  displayName: string;
  titulo: string;
  color: string;
  colorBanda: string;
  estado: string;
  posicion: 'vertical' | 'horizontal';
  disponibilidad: boolean;
  orden: number;
  imagenFrente: string;
  imagenVerso: string;
  logo: string;
  reglas: string;
  reglamento: string;
  probabilidades: string;
}

interface PublicInformationalPageDto {
  slug: string;
  title: string;
  breadcrumb: string;
  body: string;
}

/**
 * CMS-backed data source for raspaditas físicas content.
 * Replaces JsonRaspaditasFisicasContentDataSource when the CMS is live.
 */
@Injectable()
export class CmsRaspaditasFisicasContentDataSource extends RaspaditasFisicasContentDataSource {
  private readonly http = inject(HttpClient);
  private get cmsBase(): string {
    return environment.endpointBasePaths.cmsApiUrl;
  }

  getCatalogo(): Observable<RaspaditaCatalogo> {
    return this.http.get<PublicRaspaditaFisicaDto[]>(`${this.cmsBase}public/catalog/raspaditas-fisicas`).pipe(
      map(
        (games): RaspaditaCatalogo => ({
          id: 'catalogo',
          slug: 'catalogo',
          title: 'Raspaditas - Juegos',
          games: games.map((dto) => this.mapGame(dto)),
        })
      )
    );
  }

  getGanadores(slug: string): Observable<GanadorRaspadita[]> {
    return this.http.get<GanadorRaspadita[]>(`${this.cmsBase}public/catalog/raspaditas-fisicas/${slug}/ganadores`);
  }

  getPage(slug: string): Observable<ContentPage> {
    const pageSlug = `raspaditas-fisicas-${slug}`;
    return this.http
      .get<PublicInformationalPageDto>(`${this.cmsBase}public/catalog/informational-pages/${pageSlug}`)
      .pipe(
        map(
          (page): ContentPage => ({
            id: pageSlug,
            slug: pageSlug,
            title: page.title,
            breadcrumb: page.breadcrumb,
            sections: [{ type: 'innerHtml', innerHtml: page.body ?? '' }],
          })
        )
      );
  }

  private mapGame(dto: PublicRaspaditaFisicaDto): RaspaditaGame {
    return {
      id: 0, // legacy numeric field — not used for routing (slug is used)
      idJuego: dto.idJuego,
      slug: dto.slug,
      nombre: dto.nombre,
      displayName: dto.displayName,
      titulo: dto.titulo,
      color: dto.color,
      colorBanda: dto.colorBanda,
      estado: dto.estado,
      posicion: dto.posicion,
      disponibilidad: dto.disponibilidad,
      orden: dto.orden,
      imagenFrente: dto.imagenFrente,
      imagenVerso: dto.imagenVerso,
      logo: dto.logo,
      reglas: dto.reglas,
      reglamento: dto.reglamento,
      probabilidades: dto.probabilidades,
    };
  }
}
